import { PaymentLink } from "@interfaces/dws/payment";
import { tr } from "@util/tr";

export interface PaymentLinkSendRecipient {
  name?: string;
  email?: string;
  phone?: string;
}

export interface PaymentLinkSendCompany {
  name: string;
  company_email?: string;
  phone?: string;
}

export function formatPaymentLinkTemplate(template: string, ...values: any[]): string {
  return template.replace(/{(\d+)}/g, (match, index) => {
    const adjustedIndex = parseInt(index, 10) - 1;
    const value = values[adjustedIndex];
    return value !== undefined && value !== null && value !== "" ? String(value) : match;
  });
}

export function getPaymentLinkMetadata(link?: PaymentLink): Record<string, any> | null {
  const metadata = link?.payload?.["metadata"];
  if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
    return metadata as Record<string, any>;
  }
  return null;
}

/** Reservation UUID from payment-link metadata (links created from calendar/prenotazione). */
export function getPaymentLinkReservationId(link?: PaymentLink): string | null {
  const metadata = getPaymentLinkMetadata(link);
  if (!metadata) {
    return null;
  }
  const entityType = String(metadata["entity_type"] ?? metadata["entityType"] ?? "").toLowerCase();
  if (entityType !== "reservation") {
    return null;
  }
  const entityId = metadata["entity_id"] ?? metadata["entityId"];
  return entityId != null && String(entityId).trim() ? String(entityId).trim() : null;
}

export function formatReservationPaymentLinkDescription(
  experienceName: string | null | undefined,
  reservationId: string
): string {
  const name = (experienceName || "").trim();
  return name ? `${name} (${reservationId})` : reservationId;
}

export function getPaymentLinkCustomerNameFromPayload(link?: PaymentLink): string | null {
  const metadata = getPaymentLinkMetadata(link);
  if (!metadata) {
    return null;
  }
  const name = metadata["customer_name"] ?? metadata["customerName"];
  return name != null && String(name).trim() ? String(name).trim() : null;
}

export function formatContactDisplayName(contact?: {
  name?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
} | null): string {
  if (!contact) {
    return "";
  }
  const full = (contact.name || contact.fullName || "").trim();
  if (full) {
    return full;
  }
  return `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
}

export function getPaymentLinkDescription(
  link?: PaymentLink,
  reservationLabels?: Map<string, string>
): string {
  if (!link?.payload) {
    return "";
  }
  const fromPayload = link.payload["description"];
  if (fromPayload) {
    return String(fromPayload).trim();
  }
  const metadata = getPaymentLinkMetadata(link);
  if (metadata?.["description"]) {
    return String(metadata["description"]).trim();
  }
  const reservationId = getPaymentLinkReservationId(link);
  if (reservationId) {
    return reservationLabels?.get(reservationId)
      || formatReservationPaymentLinkDescription(null, reservationId);
  }
  return "";
}

export function buildManualPaymentLinkSubject(company: PaymentLinkSendCompany): string {
  return formatPaymentLinkTemplate(
    tr("payments.paymentLink.sendSubject"),
    company.name
  );
}

export function buildManualPaymentLinkMessage(
  link: PaymentLink,
  company: PaymentLinkSendCompany,
  recipient?: PaymentLinkSendRecipient
): string {
  const customerName = recipient?.name?.trim() || tr("Customer");
  const description = getPaymentLinkDescription(link);
  const descriptionBlock = description ? `${description}\n\n` : "";
  return formatPaymentLinkTemplate(
    tr("payments.paymentLink.sendMessage"),
    customerName,
    descriptionBlock,
    link.url,
    company.company_email || "",
    company.phone || tr("on our website"),
    company.name
  );
}

export function normalizeWhatsAppPhone(phone?: string): string {
  if (!phone) {
    return "";
  }
  return phone.replace(/\s/g, "").replace(/^\+/, "");
}

/** Opens email client with optional recipient; subject and body are always prefilled. */
export function openPaymentLinkEmail(to: string | undefined, subject: string, body: string): void {
  const mailtoUrl =
    (to ? `mailto:${to}` : "mailto:") +
    `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const gmailUrl =
    "https://mail.google.com/mail/?view=cm&fs=1" +
    (to ? `&to=${encodeURIComponent(to)}` : "") +
    `&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const outlookUrl = to
    ? `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIos) {
    window.location.href = mailtoUrl;
    return;
  }
  let w = window.open(gmailUrl, "_blank");
  if (!w) {
    w = window.open(outlookUrl, "_blank");
  }
  if (!w) {
    window.location.href = mailtoUrl;
  }
}

/** Opens WhatsApp Web with optional phone; message is always prefilled. */
export function openPaymentLinkWhatsApp(phone: string | undefined, message: string): void {
  const normalized = normalizeWhatsAppPhone(phone);
  const url = normalized
    ? `https://wa.me/${normalized}/?text=${encodeURIComponent(message)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
}

export function resolveSentToLabel(channel: "email" | "whatsapp", recipient?: PaymentLinkSendRecipient): string {
  if (channel === "email" && recipient?.email) {
    return recipient.email;
  }
  if (channel === "whatsapp" && recipient?.phone) {
    return recipient.phone;
  }
  return channel;
}
